import { useEffect, useMemo, useState } from "react";
import { BellRing, BellOff, Smartphone, CheckCircle2, Loader2, TestTube2, Trash2 } from "lucide-react";
import { createPushClient, isPushSupported } from "../lib/pushClient";

const EVENT_LABELS = {
  new_order: {
    title: "Nova encomenda recebida",
    desc: "Alerta imediato (verificado a cada 2 minutos) sempre que uma nova encomenda entra no sistema. Ideal para o painel Admin.",
  },
  order_pr: {
    title: "Encomenda paga (PR)",
    desc: "Alerta quando uma encomenda passa para o estado Pagamento Recebido (verificado a cada 2 minutos). Útil para acompanhar fecho de caixa em tempo real.",
  },
  new_lead: {
    title: "Novo lead atribuído",
    desc: "Receber imediatamente quando um lead for atribuído a ti (verificado a cada 5 minutos).",
  },
  stale_leads: {
    title: "Leads por contactar (7+ dias)",
    desc: "Resumo diário às 09:00 com os leads sem contacto há mais de 7 dias.",
  },
  deals_paused: {
    title: "Oportunidades paradas (14+ dias)",
    desc: "Resumo diário às 09:05 com oportunidades sem movimento há mais de 14 dias.",
  },
  level_up: {
    title: "Subidas de nível na downline",
    desc: "Quando um consultor na tua rede sobe de nível ou PIN (às 08:00). Só para uplines consultores.",
  },
  targets: {
    title: "Objectivo mensal atingido",
    desc: "Quando atinges 100% do objectivo de Volume ou Prémios do mês (verificado às 20:00).",
  },
  cliente_direto_pago: {
    title: "Cliente directo pagou encomenda",
    desc: "Recebe alerta sonoro imediatamente que um dos teus clientes directos paga uma encomenda. Ideal para acompanhar fechos de venda em tempo real.",
  },
};

const SCOPE_EVENTS = {
  admin: ["new_order", "order_pr"],
  crm: ["new_lead", "stale_leads", "deals_paused", "level_up", "targets", "cliente_direto_pago"],
  my: ["new_lead", "stale_leads", "deals_paused", "level_up", "targets", "cliente_direto_pago"],
};

/**
 * PushSettings — reusable notification preferences panel.
 * Pass `api` (axios client) and optional `scope` ("crm" | "my" | "admin").
 */
export default function PushSettings({ api, scope = "crm" }) {
  const client = useMemo(() => createPushClient(api, scope), [api, scope]);
  const supported = isPushSupported();
  const visibleEvents = SCOPE_EVENTS[scope] || Object.keys(EVENT_LABELS);

  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [prefs, setPrefs] = useState({});
  const [devices, setDevices] = useState([]);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [sub, data] = await Promise.all([
        client.isSubscribedOnThisDevice(),
        client.getPreferences(),
      ]);
      setSubscribed(!!sub);
      setPrefs(data.preferences || {});
      setDevices(data.devices || []);
    } catch (e) {
      setError(e?.message || "Falha ao carregar preferências.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (supported) load();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubscribe = async () => {
    setSubscribing(true);
    setError("");
    setInfo("");
    try {
      await client.subscribe();
      setInfo("Notificações activadas neste dispositivo.");
      await load();
    } catch (e) {
      setError(e?.message || "Falha a activar notificações.");
    } finally {
      setSubscribing(false);
    }
  };

  const onUnsubscribe = async () => {
    setSubscribing(true);
    setError("");
    setInfo("");
    try {
      await client.unsubscribe();
      setSubscribed(false);
      setInfo("Notificações desactivadas neste dispositivo.");
      await load();
    } catch (e) {
      setError(e?.message || "Falha a desactivar.");
    } finally {
      setSubscribing(false);
    }
  };

  const onTogglePref = async (ev, enabled) => {
    const prev = prefs[ev];
    setPrefs((p) => ({ ...p, [ev]: enabled }));
    try {
      await client.setPreference(ev, enabled);
    } catch (e) {
      setPrefs((p) => ({ ...p, [ev]: prev }));
      setError(e?.message || "Falha a guardar preferência.");
    }
  };

  const onTest = async () => {
    setError("");
    setInfo("");
    try {
      const r = await client.sendTest();
      setInfo(
        r.status === "no_devices"
          ? "Não há dispositivos subscritos — activa primeiro neste telemóvel."
          : `Notificação de teste enviada (${r.sent || 0} enviada(s), ${r.failed || 0} falha(s)).`
      );
    } catch (e) {
      setError(e?.message || "Falha no teste.");
    }
  };

  if (!supported) {
    return (
      <div className="rounded-2xl border border-[#E6E1D6] bg-white p-6" data-testid="push-unsupported">
        <div className="flex items-center gap-3 text-[#978D78]">
          <BellOff size={20} />
          <p className="text-sm">Este navegador não suporta Web Push. Em iOS, adiciona a app ao ecrã principal para activar (iOS 16.4+).</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="push-settings">
      <div className="rounded-2xl border border-[#E6E1D6] bg-white p-5">
        <div className="flex items-start gap-4">
          <div className={`w-11 h-11 rounded-xl grid place-items-center ${subscribed ? "bg-[#0F1829] text-[#D1CFC9]" : "bg-[#F5F2EB] text-[#978D78]"}`}>
            {subscribed ? <BellRing size={20} /> : <BellOff size={20} />}
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif" }}>
              Notificações neste dispositivo
            </h3>
            <p className="text-xs text-[#6B7280] mt-0.5">
              {subscribed
                ? "Activas. Podes desactivar em qualquer momento."
                : "Activa para receberes alertas importantes em tempo real, mesmo com a app fechada."}
            </p>
          </div>
          {subscribed ? (
            <button
              onClick={onUnsubscribe}
              disabled={subscribing}
              data-testid="push-btn-unsubscribe"
              className="px-3 py-2 text-xs rounded-full border border-[#E6E1D6] text-[#B91C1C] hover:bg-red-50 disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {subscribing ? <Loader2 size={14} className="animate-spin" /> : <BellOff size={14} />}
              Desactivar
            </button>
          ) : (
            <button
              onClick={onSubscribe}
              disabled={subscribing}
              data-testid="push-btn-subscribe"
              className="px-3 py-2 text-xs rounded-full bg-[#0F1829] text-white hover:bg-[#1a2542] disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {subscribing ? <Loader2 size={14} className="animate-spin" /> : <BellRing size={14} />}
              Activar
            </button>
          )}
        </div>

        {subscribed && (
          <div className="mt-4 flex items-center justify-end">
            <button
              onClick={onTest}
              data-testid="push-btn-test"
              className="px-3 py-1.5 text-xs rounded-full text-[#0F1829] hover:bg-[#F5F2EB] inline-flex items-center gap-1.5"
            >
              <TestTube2 size={14} /> Enviar notificação de teste
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800" data-testid="push-error">
          {error}
        </div>
      )}
      {info && (
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-800 inline-flex items-center gap-2" data-testid="push-info">
          <CheckCircle2 size={16} /> {info}
        </div>
      )}

      <div className="rounded-2xl border border-[#E6E1D6] bg-white p-5">
        <h3 className="text-base font-semibold text-[#0F1829] mb-1" style={{ fontFamily: "'Fraunces', serif" }}>
          Tipos de notificação
        </h3>
        <p className="text-xs text-[#6B7280] mb-4">Escolhe quais alertas queres receber.</p>

        {loading ? (
          <div className="flex items-center gap-2 text-[#978D78] text-sm">
            <Loader2 size={14} className="animate-spin" /> A carregar…
          </div>
        ) : (
          <div className="space-y-3">
            {Object.entries(EVENT_LABELS)
              .filter(([ev]) => visibleEvents.includes(ev))
              .map(([ev, meta]) => {
              const enabled = prefs[ev] !== false; // default true
              return (
                <label
                  key={ev}
                  data-testid={`push-pref-${ev}`}
                  className="flex items-start gap-3 p-3 rounded-xl hover:bg-[#FAF8F3] cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={enabled}
                    onChange={(e) => onTogglePref(ev, e.target.checked)}
                    data-testid={`push-pref-${ev}-checkbox`}
                    className="mt-0.5 w-4 h-4 accent-[#0F1829]"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#0F1829]">{meta.title}</p>
                    <p className="text-xs text-[#6B7280] mt-0.5">{meta.desc}</p>
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </div>

      {devices.length > 0 && (
        <div className="rounded-2xl border border-[#E6E1D6] bg-white p-5">
          <h3 className="text-base font-semibold text-[#0F1829] mb-3" style={{ fontFamily: "'Fraunces', serif" }}>
            Dispositivos subscritos
          </h3>
          <ul className="space-y-2" data-testid="push-devices">
            {devices.map((d) => (
              <li key={d.device_id} className="flex items-start gap-3 p-2 rounded-lg hover:bg-[#FAF8F3]">
                <Smartphone size={16} className="text-[#978D78] mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-[#0F1829] truncate">{(d.user_agent || "Dispositivo").slice(0, 90)}</p>
                  <p className="text-[10px] text-[#978D78]">
                    {d.is_active ? "activo" : "inactivo"} • {d.scope || "crm"} • actualizado {d.updated_at?.slice(0, 10)}
                  </p>
                </div>
                {d.is_active === false && <Trash2 size={14} className="text-[#978D78]" />}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
